/* Sham Volume Tiers - the only place a volume tier or a cent is decided.
   Pure and unit tested. Liquid supplies line-level truth as data attributes;
   this module does the arithmetic. Consumers read it off the global. */

(function (global) {
  // Mirrors loop-volume-sync/src/tiers.js. A ladder that service refuses
  // must not render on the storefront: renewals would keep arming from the
  // previous ladder while the display promised the new one.
  var MAX_TIER_PERCENTAGE = 20;

  var LADDER_ELEMENT_ID = 'sham-volume-ladder';

  function fail(message) {
    throw new Error('tmc_meta.volume_tiers: ' + message);
  }

  function parseTiers(value) {
    if (typeof value !== 'string' || value.length === 0) fail('empty or non-string value');

    var parsed;
    try {
      parsed = JSON.parse(value);
    } catch (e) {
      fail('not valid JSON');
    }

    if (!parsed || !Array.isArray(parsed.tiers) || parsed.tiers.length === 0) {
      fail('must contain a non-empty tiers array');
    }

    var tiers = parsed.tiers.map(function (t) {
      if (!Number.isInteger(t.minQuantity) || t.minQuantity < 1) {
        fail('minQuantity must be an integer >= 1, got ' + t.minQuantity);
      }
      if (!Number.isInteger(t.percentage) || t.percentage < 1 || t.percentage > MAX_TIER_PERCENTAGE) {
        fail('percentage must be between 1 and ' + MAX_TIER_PERCENTAGE + ', got ' + t.percentage);
      }
      return { minQuantity: t.minQuantity, percentage: t.percentage };
    });

    tiers.sort(function (a, b) {
      return b.minQuantity - a.minQuantity;
    });

    for (var i = 0; i < tiers.length - 1; i++) {
      var higher = tiers[i];
      var lower = tiers[i + 1];
      if (higher.minQuantity === lower.minQuantity) {
        fail('duplicate minQuantity ' + higher.minQuantity);
      }
      if (higher.percentage <= lower.percentage) {
        fail(
          'non-monotonic ladder - minQuantity ' + higher.minQuantity +
          ' earns ' + higher.percentage + '% while ' + lower.minQuantity +
          ' earns ' + lower.percentage + '%'
        );
      }
    }

    return tiers;
  }

  // tiers arrive sorted descending, so the first match is the highest tier.
  function selectTier(tiers, quantity) {
    if (!tiers) return null;
    for (var i = 0; i < tiers.length; i++) {
      if (quantity >= tiers[i].minQuantity) return tiers[i];
    }
    return null;
  }

  function nextTier(tiers, quantity) {
    if (!tiers) return null;
    var candidate = null;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].minQuantity > quantity) candidate = tiers[i];
    }
    return candidate;
  }

  // Distance to the NEXT threshold only - resets to 0 at every tier reached,
  // rather than accumulating toward the top of the ladder. 100 at the top
  // tier, where there is no next threshold to progress toward.
  function progressPercentage(tiers, quantity) {
    var reached = selectTier(tiers, quantity);
    var upcoming = nextTier(tiers, quantity);
    if (!upcoming) return 100;
    var prevThreshold = reached ? reached.minQuantity : 0;
    return Math.round(((quantity - prevThreshold) / (upcoming.minQuantity - prevThreshold)) * 100);
  }

  // Integer-only half-up. Every input is integer cents, so no float ever
  // enters the arithmetic: 28.5 * 0.05 is 1.4250000000000003 in float and
  // relying on that rounding correctly is how a 1c bug lands on some
  // prices and not others.
  function halfUpDiv(numerator, denominator) {
    return Math.floor((2 * numerator + denominator) / (2 * denominator));
  }

  function lineDiscountCents(baseCents, quantity, tiers) {
    var grossCents = baseCents * quantity;
    var tier = selectTier(tiers, quantity);
    if (!tier) {
      return { percentage: 0, grossCents: grossCents, discountCents: 0, netCents: grossCents };
    }
    // Round once, on the line total. Rounding per unit and multiplying
    // gives 715 where the line gives 713 on a qty-5 $28.50 line at 5%.
    var discountCents = halfUpDiv(grossCents * tier.percentage, 100);
    return {
      percentage: tier.percentage,
      grossCents: grossCents,
      discountCents: discountCents,
      netCents: grossCents - discountCents,
    };
  }

  // Absence is the flag-off state, so it returns null rather than throwing.
  // A malformed ladder also returns null: refusing to render is correct, and
  // an exception here would take out whichever handler called us.
  function readLadder(doc) {
    var el = doc && doc.getElementById ? doc.getElementById(LADDER_ELEMENT_ID) : null;
    if (!el) return null;
    try {
      return parseTiers(el.textContent);
    } catch (e) {
      if (global.console && global.console.error) {
        global.console.error('[sham-volume-tiers] refusing to render: ' + e.message);
      }
      return null;
    }
  }

  // Writes the slot's state, text and pill. Every percentage and threshold
  // comes from the ladder. Callers pass integer cents and a line quantity.
  //
  // root.querySelector resolves the FIRST [data-volume-slot]/[data-volume-pill]
  // under root. That is wrong wherever a root can contain more than one
  // candidate (e.g. a footer with several per-plan rows, only one visible) -
  // pass the exact nodes as opts.slotEl / opts.pillEl there instead of
  // trusting the querySelector fallback.
  function renderSlot(root, opts) {
    var slot = opts.slotEl !== undefined ? opts.slotEl : root.querySelector('[data-volume-slot]');
    var pill = opts.pillEl !== undefined ? opts.pillEl : root.querySelector('[data-volume-pill]');
    var ladder = opts.ladder;

    if (!ladder) {
      if (slot) slot.textContent = '';
      if (pill) pill.style.display = 'none';
      return null;
    }

    var qty = opts.quantity;
    var line = lineDiscountCents(opts.baseCents, qty, ladder);
    var upcoming = nextTier(ladder, qty);

    if (slot) {
      var state, text;
      if (qty === 0) {
        state = 'hint';
        text = ladder
          .slice()
          .reverse()
          .map(function (t) { return t.percentage + '% off ' + t.minQuantity; })
          .join(' · ');
      } else if (upcoming) {
        state = 'nudge';
        // "Add N more, save X%" - the progress bar supplies the context
        // ("more" of what) that the words alone lack.
        text = 'Add ' + (upcoming.minQuantity - qty) + ' more, save ' + upcoming.percentage + '%';
      } else {
        state = 'confirm';
        text = '🎉 Saving ' + line.percentage + '%';
      }
      slot.className = 'sham-volume-slot sham-volume-slot--' + state;
      slot.setAttribute('data-volume-slot-state', state);
      slot.textContent = text;
      slot.style.setProperty('--sham-volume-progress', progressPercentage(ladder, qty) + '%');
    }

    if (pill) {
      if (line.percentage > 0) {
        pill.textContent = '−' + line.percentage + '%';
        pill.style.display = '';
      } else {
        pill.style.display = 'none';
      }
      popPillOnUpgrade(pill, line.percentage);
    }

    return line;
  }

  // Pops the pill when the applied discount rises versus the last render,
  // never on a decrease and never on the first render of a card (no prior
  // value to compare against, which would otherwise pop every card on load).
  function popPillOnUpgrade(pill, percentage) {
    if (!pill || !pill.dataset) return;
    var prev = pill.dataset.shamPrevPercentage;
    if (prev !== undefined && percentage > Number(prev)) {
      if (pill.classList) {
        // Same class each time, so force a reflow between remove and
        // add or a repeat upgrade in one session would not replay it.
        pill.classList.remove('sham-volume-pill--pop');
        void pill.offsetWidth;
        pill.classList.add('sham-volume-pill--pop');
      }
    }
    pill.dataset.shamPrevPercentage = String(percentage);
  }

  var api = {
    MAX_TIER_PERCENTAGE: MAX_TIER_PERCENTAGE,
    LADDER_ELEMENT_ID: LADDER_ELEMENT_ID,
    parseTiers: parseTiers,
    selectTier: selectTier,
    nextTier: nextTier,
    progressPercentage: progressPercentage,
    lineDiscountCents: lineDiscountCents,
    readLadder: readLadder,
    renderSlot: renderSlot,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else global.ShamVolumeTiers = api;

})(typeof globalThis !== 'undefined' ? globalThis : this);
